import {
  Button,
  CTABlockContainer,
  CTAFrame,
  CTASubtitle,
  CTATitle,
  CategoryHeaderContainer,
  CategoryHeaderSubtitle,
  CategoryHeaderTitle,
  LANDING_SOCIAL_TESTIMONIALS,
  PartnersLanding,
  SocialTestimonialBlock,
} from '@cointracker/styleguide';
import { LandingCTATitle } from '@cointracker/styleguide/src/CTABlock/CTABlock';
import { CategoryHeaderCopy } from '@cointracker/styleguide/src/CategoryHeader/CategoryHeader';
import { Grid } from '@cointracker/styleguide/src/Grid/Grid.jsx';
import coinbaseIconUrl from '@cointracker/styleguide/src/icons/partners/Coinbase.svg?url';
import hrBlockIconUrl from '@cointracker/styleguide/src/icons/partners/H&R Block.svg?url';
import intuitIconUrl from '@cointracker/styleguide/src/icons/partners/Intuit TurboTax.svg?url';
import metamaskIconUrl from '@cointracker/styleguide/src/icons/partners/MetaMask.svg?url';
import openseaIconUrl from '@cointracker/styleguide/src/icons/partners/OpenSea.svg?url';
import phantomIconUrl from '@cointracker/styleguide/src/icons/partners/Phantom.svg?url';
import solanaIconUrl from '@cointracker/styleguide/src/icons/partners/Solana Foundation.svg?url';
import uniswapIconUrl from '@cointracker/styleguide/src/icons/partners/Uniswap.svg?url';
import { REBRAND_URLS, useSearchParams } from '@cointracker/ui';
import classNames from 'clsx';
import { trackSignUpButtonClicked } from 'src/common/analytics';
import fileInMinutesUrl from '../../../assets/rebrand/lottie/1 - File in minutes.js';
import complexityToClarityUrl from '../../../assets/rebrand/lottie/2 - Complexity to clarity.js';
import comprehensiveCoverageUrl from '../../../assets/rebrand/lottie/3 - Comprehensive coverage.js';
import saveMoneyBoostWealthUrl from '../../../assets/rebrand/lottie/4 - Save money, boost wealth.js';
import {
  buildGetStartedUrl,
  getNextUrl,
} from '../../../pages-react/landing/utils';
import { FAQAccordion } from '../FAQAccordion';
import { CallToActionButton } from '../LandingCTAs';
import {
  LandingFeature,
  LandingFeatureButton,
  LandingFeatureContainer,
  LandingFeatureSubtitle,
  LandingFeatureTitle,
} from '../LandingFeature';
import { useFeatureState } from '../LandingFeature/utils';
import { partnersContainer } from '../LandingFlow/LandingFlow.css';
import { CalculatorArea } from './CalculatorArea';
const FEATURES_SECTIONS = ['Speed', 'Ease', 'Accuracy', 'Growth'];

export const CalculatorLanding = () => {
  const [searchParams] = useSearchParams();
  const nextUrl = getNextUrl(searchParams);
  const getStartedUrl = buildGetStartedUrl(nextUrl);

  const { ref: fileInMinutesRef, state: fileInMinutesState } =
    useFeatureState();
  const { ref: complexityToClarityRef, state: complexityToClarityState } =
    useFeatureState();
  const { ref: comprehensiveCoverageRef, state: comprehensiveCoverageState } =
    useFeatureState();
  const { ref: saveMoneyBoostWealthRef, state: saveMoneyBoostWealthState } =
    useFeatureState();

  const trackSignupAndRedirect = (
    buttonText: string,
    sectionName: string,
    sectionTitle: string,
  ) => {
    trackSignUpButtonClicked(
      buttonText,
      sectionName,
      sectionTitle,
      'Calculator',
    );
    window.location.href = getStartedUrl;
  };

  return (
    <>
      <Grid>
        <CategoryHeaderContainer
          style={{
            marginLeft: 'auto',
            marginRight: 'auto',
            maxWidth: '1440px',
          }}
        >
          <CategoryHeaderCopy>
            <CategoryHeaderTitle>Profit/loss calculator</CategoryHeaderTitle>
            <CategoryHeaderSubtitle>
              Enter the details of your crypto trade and find out how much you
              gained or lost. Want to know what this means when it comes to tax
              time?
            </CategoryHeaderSubtitle>
            <Button
              className="mt-32 max-w-fit"
              onClick={() =>
                trackSignupAndRedirect(
                  'Start for Free',
                  'P/L Calculator Hero',
                  'Profit/loss calculator',
                )
              }
            >
              Start for Free
            </Button>
          </CategoryHeaderCopy>
        </CategoryHeaderContainer>
      </Grid>
      <CalculatorArea />

      <CTABlockContainer>
        <LandingCTATitle>
          Making crypto taxes <br /> pain-free since 2017
        </LandingCTATitle>
      </CTABlockContainer>

      <LandingFeatureContainer>
        {/* TODO: update button onclick params */}
        <div ref={fileInMinutesRef}>
          <LandingFeature
            sections={FEATURES_SECTIONS}
            sectionIndex={0}
            imgAlt="File in minutes"
            lottieData={fileInMinutesUrl}
            snapState={fileInMinutesState}
            isLottie
            noFadeIn
          >
            <LandingFeatureTitle>File in minutes</LandingFeatureTitle>
            <LandingFeatureSubtitle>
              CoinTracker is designed for efficiency so that you can file with
              TurboTax, H&R Block, or your own tax professional
            </LandingFeatureSubtitle>
            <LandingFeatureButton
              onClick={() => trackSignupAndRedirect('todo', 'todo', 'todo')}
            >
              See more
            </LandingFeatureButton>
          </LandingFeature>
        </div>

        <div ref={complexityToClarityRef}>
          <LandingFeature
            sections={FEATURES_SECTIONS}
            sectionIndex={1}
            imgAlt="Clarity, not complexity"
            lottieData={complexityToClarityUrl}
            snapState={complexityToClarityState}
            isLottie
          >
            <LandingFeatureTitle>Clarity, not complexity</LandingFeatureTitle>
            <LandingFeatureSubtitle>
              View all your crypto activity in one place and get a clear picture
              of your balance, performance, and taxes. Automatically
            </LandingFeatureSubtitle>
            <LandingFeatureButton
              onClick={() => trackSignupAndRedirect('todo', 'todo', 'todo')}
            >
              See more
            </LandingFeatureButton>
          </LandingFeature>
        </div>

        <div ref={comprehensiveCoverageRef}>
          <LandingFeature
            sections={FEATURES_SECTIONS}
            sectionIndex={2}
            imgAlt="Comprehensive coverage"
            lottieData={comprehensiveCoverageUrl}
            snapState={comprehensiveCoverageState}
            isLottie
          >
            <LandingFeatureTitle>Comprehensive coverage</LandingFeatureTitle>
            <LandingFeatureSubtitle>
              CoinTracker auto-categorizes your latest DeFi transactions,
              removes spam, and integrates with 10,000 crypto assets to ensure
              your taxes are done right
            </LandingFeatureSubtitle>
            <LandingFeatureButton
              onClick={() => trackSignupAndRedirect('todo', 'todo', 'todo')}
            >
              See more
            </LandingFeatureButton>
          </LandingFeature>
        </div>

        <div ref={saveMoneyBoostWealthRef}>
          <LandingFeature
            sections={FEATURES_SECTIONS}
            sectionIndex={3}
            imgAlt="Save money, boost wealth"
            lottieData={saveMoneyBoostWealthUrl}
            snapState={saveMoneyBoostWealthState}
            isLottie
            noFadeOut
          >
            <LandingFeatureTitle>Save money, boost wealth</LandingFeatureTitle>
            <LandingFeatureSubtitle>
              CoinTracker optimizes your crypto portfolio with personalized
              insights and performance-tracking tools. Stay informed all year
              round with more money back in your pocket
            </LandingFeatureSubtitle>
            <LandingFeatureButton
              onClick={() => trackSignupAndRedirect('todo', 'todo', 'todo')}
            >
              See more
            </LandingFeatureButton>
          </LandingFeature>
        </div>
      </LandingFeatureContainer>

      <FAQAccordion className="mt-120" />

      <div className={classNames(partnersContainer)}>
        <PartnersLanding
          title="Official tax partners"
          subtitle="We partner with the best to make tax time a breeze"
          topPartners={[
            { logoUrl: coinbaseIconUrl, text: 'Coinbase' },
            { logoUrl: intuitIconUrl, text: 'Intuit TurboTax' },
            { logoUrl: hrBlockIconUrl, text: 'H&R Block' },
            { logoUrl: uniswapIconUrl, text: 'Uniswap' },
          ]}
          bottomPartners={[
            { logoUrl: openseaIconUrl, text: 'Open Sea' },
            { logoUrl: metamaskIconUrl, text: 'MetaMask' },
            { logoUrl: phantomIconUrl, text: 'Phantom' },
            { logoUrl: solanaIconUrl, text: 'Solana Foundation' },
          ]}
        />
      </div>

      <SocialTestimonialBlock
        alignLeft
        title="Don't just take our word for it"
        subtext="What our customers are saying"
        testimonials={LANDING_SOCIAL_TESTIMONIALS}
        animated
      />

      <CTABlockContainer framed>
        <CTAFrame>
          <CTATitle>
            Save time.
            <br />
            Save money.
          </CTATitle>
          <CTASubtitle>
            Join the 2.5M+ customers who trust CoinTracker with their crypto
            taxes
          </CTASubtitle>
          <CallToActionButton
            href={REBRAND_URLS.GET_STARTED}
            buttonText="Start for free"
            pageTitle="Calculator"
            sectionName="SaveTimeMoneyCTA"
            sectionTitle="Save time. Save money."
          >
            Start for free
          </CallToActionButton>
        </CTAFrame>
      </CTABlockContainer>
    </>
  );
};
